import { BookOpen, ChevronDown, Menu, Moon, ShieldCheck, Sun, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { MonitoringRelease, ReleaseIndex } from "../data/monitoring";

export const NAV_ITEMS = [
  ["executive-brief", "Executive Brief"], ["new-themes", "Newly Added Themes"], ["theme-overview", "Theme Overview"],
  ["conversation-landscape", "Conversation Landscape"], ["theme-momentum", "Theme Momentum"], ["connected-conversation", "Connected Conversation"], ["cohort-analysis", "Cohort Analysis"],
  ["method-appendix", "Appendix"],
] as const;

export type NavItem = typeof NAV_ITEMS[number];
export type NavId = NavItem[0];

export function navItemsForRelease(release: MonitoringRelease): readonly NavItem[] {
  return release.manifest.variant === "current_wave"
    ? NAV_ITEMS.filter(([id]) => id !== "new-themes")
    : NAV_ITEMS;
}

export function useScrollSpy(enabled: boolean): [NavId, (id: NavId) => void] {
  const [active, setActive] = useState<NavId>("executive-brief");
  const selectedByNavigation = useRef<NavId | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let frame: number | undefined;
    const updateActiveSection = () => {
      frame = undefined;
      // The active section is the last section that has passed the reading line.
      // This remains stable while a long section is in view, unlike competing
      // IntersectionObserver callbacks from adjacent sections.
      const readingLine = 132;
      const sections = NAV_ITEMS
        .map(([id]) => document.getElementById(id))
        .filter((section): section is HTMLElement => Boolean(section));
      const current = sections.filter((section) => section.getBoundingClientRect().top <= readingLine).at(-1) ?? sections[0];
      if (current) setActive(current.id as NavId);
    };
    const onScroll = () => {
      if (selectedByNavigation.current) return;
      if (frame === undefined) frame = window.requestAnimationFrame(updateActiveSection);
    };
    const onReaderScrollIntent = () => {
      if (!selectedByNavigation.current) return;
      selectedByNavigation.current = null;
      onScroll();
    };
    updateActiveSection();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    window.addEventListener("wheel", onReaderScrollIntent, { passive: true });
    window.addEventListener("touchstart", onReaderScrollIntent, { passive: true });
    window.addEventListener("pointerdown", onReaderScrollIntent, { passive: true });
    window.addEventListener("keydown", onReaderScrollIntent);
    return () => { window.removeEventListener("scroll", onScroll); window.removeEventListener("resize", onScroll); window.removeEventListener("wheel", onReaderScrollIntent); window.removeEventListener("touchstart", onReaderScrollIntent); window.removeEventListener("pointerdown", onReaderScrollIntent); window.removeEventListener("keydown", onReaderScrollIntent); if (frame !== undefined) window.cancelAnimationFrame(frame); };
  }, [enabled]);
  return [active, (id) => { selectedByNavigation.current = id; setActive(id); }];
}

interface Props { release: MonitoringRelease; index: ReleaseIndex; active: NavId; onSectionSelect: (id: NavId) => void; theme: "light" | "dark"; onThemeChange: (value: "light" | "dark") => void; }

function ThemeToggle({ theme, onThemeChange }: Pick<Props, "theme" | "onThemeChange">) {
  const dark = theme === "dark";
  return <button className="theme-toggle" type="button" aria-pressed={dark} onClick={() => onThemeChange(dark ? "light" : "dark")}><>{dark ? <Sun size={16} /> : <Moon size={16} />}</><span>{dark ? "Light mode" : "Dark mode"}</span></button>;
}

function NavigationContent({ release, index, active, onSectionSelect, theme, onThemeChange, close, includeThemeToggle = true }: Props & { close?: () => void; includeThemeToggle?: boolean }) {
  const navigate = useNavigate();
  const base = `/reports/${release.manifest.release_id}`;
  const navItems = navItemsForRelease(release);
  const jumpToSection = (event: React.MouseEvent<HTMLAnchorElement>, id: NavId) => {
    event.preventDefault();
    const section = document.getElementById(id);
    if (!section) {
      navigate(`${base}#${id}`);
      close?.();
      return;
    }
    onSectionSelect(id);
    window.history.pushState(null, "", `${base}#${id}`);
    section.scrollIntoView({ behavior: "smooth", block: "start" });
    close?.();
  };
  return <>
    <div className="rail-brand">
      {release.manifest.logo_url ? <img className="game-mark game-mark--lg" src={`${import.meta.env.BASE_URL}${release.manifest.logo_url}`} alt={release.manifest.logo_alt} /> : <span className="game-mark game-mark--lg game-mark--fallback">OW</span>}
      <div><span className="rail-brand__kicker">Conversation Intelligence</span><strong>{release.manifest.client_name || release.manifest.title}</strong></div>
    </div>
    <label className="release-selector"><span>Report Edition</span><span className="release-selector__control"><select value={release.manifest.release_id} onChange={(event) => navigate(`/reports/${event.target.value}`)} aria-label="Select Report Edition">{index.releases.map((item) => <option value={item.release_id} key={item.release_id}>{item.label}</option>)}</select><ChevronDown size={15} /></span></label>
    <div className="rail-meta"><div><span>Data Through</span><strong>{release.manifest.current_window}</strong></div><div><span>{release.manifest.variant === "comparison" ? "Comparison" : "Report type"}</span><strong>{release.manifest.variant === "comparison" ? release.manifest.baseline_window : "Current-Wave Read"}</strong></div></div>
    <nav className="report-nav" aria-label="Report sections"><div className="report-nav__progress"><span style={{ height: `${((navItems.findIndex(([id]) => id === active) + 1) / navItems.length) * 100}%` }} /></div><ol>{navItems.map(([id, label], position) => <li key={id}><a href={`${base}#${id}`} className={active === id ? "is-active" : ""} onClick={(event) => jumpToSection(event, id)}><span>{String(position + 1).padStart(2, "0")}</span>{label}</a></li>)}</ol></nav>
    <div className="rail-actions"><Link to={`${base}/appendix`} onClick={close}><BookOpen size={16} />Full Appendix</Link></div><div className="rail-security"><ShieldCheck size={14} />Private Client Report</div>
    {includeThemeToggle && <ThemeToggle theme={theme} onThemeChange={onThemeChange} />}
  </>;
}

export function SideRail(props: Props) { return <aside className="side-rail"><div className="side-rail__content"><NavigationContent {...props} includeThemeToggle={false} /></div><ThemeToggle theme={props.theme} onThemeChange={props.onThemeChange} /></aside>; }

export function MobileNavigation(props: Props) {
  const [open, setOpen] = useState(false); const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => { if (!open) return; document.body.classList.add("drawer-open"); closeRef.current?.focus(); return () => document.body.classList.remove("drawer-open"); }, [open]);
  const label = navItemsForRelease(props.release).find(([id]) => id === props.active)?.[1];
  return <><header className="mobile-nav"><button type="button" onClick={() => setOpen(true)}><Menu size={18} /><span><small>Jump to section</small><strong>{label}</strong></span></button><span className="game-mark game-mark--sm game-mark--fallback">OW</span></header>{open && <button className="drawer-backdrop" type="button" onClick={() => setOpen(false)} aria-label="Close section navigation" />}{open && <aside className="mobile-drawer is-open"><button ref={closeRef} className="drawer-close" type="button" onClick={() => setOpen(false)} aria-label="Close section navigation"><X /></button><NavigationContent {...props} close={() => setOpen(false)} /></aside>}</>;
}
